import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Grid } from "@mui/material";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { formatDate } from "../utils/formatters";
import moment from "moment";
import AddDeviceDialog from "../components/Dialogs/addDevice";

// Dashboard components
import HomeHeader from "../components/Dashboard/HomeHeader";
import DeviceStatsGrid from "../components/Dashboard/DeviceStatsGrid";
import RecentAlertsPanel from "../components/Dashboard/RecentAlertsPanel";
import QuickActionsPanel from "../components/Dashboard/QuickActionsPanel";
import LowBatteryPanel from "../components/Dashboard/LowBatteryPanel";

// TanStack Query hooks
import { useDevices } from "../hooks/queries/useDevices";
import { useOrganisationAlerts } from "../hooks/queries/useAlerts";
import { useOrganisationRoles } from "../hooks/queries/useOrganisations";
import { getRefreshInterval } from "../utils/refreshInterval";

// Custom hooks
import {
  useDeviceStats,
  useRecentAlerts,
  useCanAddDevice,
  useIsDataLoaded,
} from "../hooks/useHomeData";
import { UtilityService } from "../services/UtilityService";
import { OrganisationService } from "../services/OrganisationService";
import { DataService } from "../services/DataService";

function Home() {
  const { t } = useTranslation("common");
  const { user } = useAuth0();
  const navigate = useNavigate();

  // Redux state (keeping these for now)
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const userDB = useSelector((state) => state.user);

  // Fetch organisation roles using TanStack Query (this is the correct data source)
  const { data: organisationRoles = [] } = useOrganisationRoles(
    selectedOrganisation?.id
  );

  // Ensure organisationRoles is always an array
  const safeUserOrganisationRoles = Array.isArray(organisationRoles)
    ? organisationRoles
    : [];

  // TanStack Query data (useDevices handles refreshInterval internally)
  const { data: devices = [] } = useDevices(selectedOrganisation?.id);

  // Get user refresh rate from localStorage for alert history
  const refreshInterval = getRefreshInterval();
  const { data: organisationAlerts = [] } = useOrganisationAlerts(
    selectedOrganisation?.id,
    refreshInterval
  );

  // Local state
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);

  // Custom hooks for computed values
  const deviceStats = useDeviceStats(devices);
  const recentAlerts = useRecentAlerts(organisationAlerts, devices);
  const canAddDevice = useCanAddDevice(
    selectedOrganisation,
    userDB,
    safeUserOrganisationRoles,
    OrganisationService.hasPermission
  );
  const isDataLoaded = useIsDataLoaded(devices, organisationAlerts);

  // Handle device dialog close
  const handleDeviceDialogClose = useCallback(() => {
    setAddDeviceOpen(false);
    // Note: handleRefresh will be called manually when dialog closes
  }, []);

  useEffect(() => {
    UtilityService.setTitle("Home");
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (user?.email && user?.sub) {
      await DataService.refreshData(user.email, user.sub);
      UtilityService.updateSnackbarMessage(
        t("common.refreshed") + " " + formatDate(moment())
      );
    }
  }, [user, t]);

  // Handle device navigation
  const handleDeviceClick = useCallback(
    (deviceId) => {
      navigate(`/device/${deviceId}`);
    },
    [navigate]
  );

  // Handle add device click
  const handleAddDeviceClick = useCallback(() => {
    setAddDeviceOpen(true);
  }, []);

  return (
    <>
      {/* Header */}
      <HomeHeader
        selectedOrganisation={selectedOrganisation}
        onRefresh={handleRefresh}
      />

      {/* Stats Cards */}
      <DeviceStatsGrid
        devices={devices}
        userDB={userDB}
        deviceStats={deviceStats}
      />

      {/* Recent Activity */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <RecentAlertsPanel
            recentAlerts={recentAlerts}
            isDataLoaded={isDataLoaded}
            onDeviceClick={handleDeviceClick}
          />
        </Grid>

        {/* Quick Actions Sidebar */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <QuickActionsPanel
            canAddDevice={canAddDevice}
            userDB={userDB}
            onAddDeviceClick={handleAddDeviceClick}
          />
        </Grid>
      </Grid>

      {/* Low Battery Panel */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={{ xs: 12 }}>
          <LowBatteryPanel devices={devices} />
        </Grid>
      </Grid>

      <AddDeviceDialog
        open={addDeviceOpen}
        handleClose={handleDeviceDialogClose}
      />
    </>
  );
}

export default withAuthenticationRequired(Home);
